import AuthDialog from '@/components/AuthDialog'
import { useAuth } from '@/context/auth'
import { getConfig } from '@/lib/config'
import { createContext, ReactNode, useContext, useEffect, useReducer } from 'react'

type ActionType = {
  type: 'TOGGLE_AUTH_DIALOG'
}

type ContextType = {
  showAuthPrompt: boolean
  toggleAuthPrompt: () => void
}

const contextDefaultValues: ContextType = {
  showAuthPrompt: false,
  toggleAuthPrompt: () => {},
}

const AuthPromptContext = createContext<ContextType>(contextDefaultValues)

type Props = {
  children: ReactNode
  disableAuthPrompt?: boolean
  persistentAuthPrompt?: boolean
  delayAuthPrompt?: number // delay in milliseconds
}

const { publicRuntimeConfig } = getConfig()

function authPromptReducer(state: ContextType, action: ActionType): ContextType {
  switch (action.type) {
    case 'TOGGLE_AUTH_DIALOG':
      return {
        ...state,
        showAuthPrompt: !state.showAuthPrompt,
      }

    default:
      return state
  }
}

export function AuthPromptProvider({
  children,
  disableAuthPrompt = false,
  persistentAuthPrompt = false,
  delayAuthPrompt,
}: Props): JSX.Element {
  const {
    state: { user, ready: authStateReady },
  } = useAuth()
  const [state, dispatch] = useReducer(authPromptReducer, contextDefaultValues)

  const contextValue = {
    showAuthPrompt: state.showAuthPrompt,
    toggleAuthPrompt: () => {
      dispatch({ type: 'TOGGLE_AUTH_DIALOG' })
    },
  }

  useEffect(() => {
    if (!authStateReady || user?.isVerified || disableAuthPrompt) return

    const timer = setTimeout(() => {
      contextValue.toggleAuthPrompt()
    }, delayAuthPrompt ?? publicRuntimeConfig.dialogs.auth.delay)

    return () => clearTimeout(timer)
  }, [authStateReady, user])

  return (
    <AuthPromptContext.Provider value={contextValue}>
      {state.showAuthPrompt && <AuthDialog persistent={persistentAuthPrompt} />}
      {children}
    </AuthPromptContext.Provider>
  )
}

export function useAuthPrompt(): ContextType {
  const context = useContext(AuthPromptContext)
  if (context === undefined) {
    throw new Error('useAuthPrompt must be used within a AuthPromptProvider')
  }
  return context
}
